import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Download, Search, ArrowRight, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, PeriodInput, Loading, Empty, StatPill } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { ImportDialog } from "@/components/ImportDialog";
import { RecapLockBanner, PeriodStageBar } from "@/components/RecapLockBanner";
import { CustStatusBadge } from "@/components/badges";
import { NativeSelect } from "@/components/common";
import { CUST_STATUS } from "@/lib/format";
import { DebtBadge, PaymentDialog } from "@/components/PaymentDialog";
import { PaidCorrectDialog } from "@/components/PaidCorrectDialog";
import { api, errMsg, downloadCsv } from "@/lib/api";
import { colLetter, currentPeriod, num, periodLabel, rupiah } from "@/lib/format";

export default function Recap() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [lock, setLock] = useState(null);
  const editable = can("recap:write") && lock && !lock.locked;
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [cells, setCells] = useState({});
  const [dirty, setDirty] = useState({});
  const [q, setQ] = useState("");
  const [stF, setStF] = useState("");
  const [statuses, setStatuses] = useState({});
  const [busy, setBusy] = useState(false);
  const [debts, setDebts] = useState({});
  const [snap, setSnap] = useState({});
  const [payTarget, setPayTarget] = useState(null);
  const [fixTarget, setFixTarget] = useState(null);
  const loadDebts = useCallback(async () => {
    if (!can("debt:read")) return;
    try { setDebts((await api.get("/debts/summary", { params: { period } })).data); } catch (e) { toast.error(errMsg(e)); }
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const id = ++reqId.current;
    setData(null);
    setLock(null);
    try {
      const [c, v, r] = await Promise.all([api.get("/customers"), api.get("/prices"), api.get("/recaps", { params: { period } })]);
      if (id !== reqId.current) return;
      const map = {};
      const pm = {};
      r.data.entries.forEach((e) => { map[`${e.customer_id}|${e.voucher_type_id}`] = e.qty; if (e.price != null) pm[`${e.customer_id}|${e.voucher_type_id}`] = e.price; });
      setSnap(pm);
      const used = new Set(r.data.entries.map((e) => e.customer_id));
      const sts = r.data.statuses || {};
      setStatuses(sts);
      setData({ customers: c.data.filter((x) => (x.active && !sts[x.id]?.stopped_before) || used.has(x.id)), types: v.data.filter((x) => x.active) });
      setCells(map);
      setLock(r.data.lock);
      setDirty({});
    } catch (e) { toast.error(errMsg(e)); }
  }, [period]);
  useEffect(() => {
    load();
    loadDebts();
  }, [load, loadDebts]);

  const rows = useMemo(() => (data ? data.customers.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) && (!stF || (statuses[c.id]?.status || "aktif") === stF)) : []), [data, q, stF, statuses]);
  const setCell = (k, v) => {
    const n = v === "" ? 0 : Math.max(0, parseInt(v, 10) || 0);
    setCells((s) => ({ ...s, [k]: n }));
    setDirty((s) => ({ ...s, [k]: true }));
  };
  const save = async () => {
    const entries = Object.keys(dirty).map((k) => {
      const [customer_id, voucher_type_id] = k.split("|");
      return { customer_id, voucher_type_id, qty: cells[k] || 0 };
    });
    setBusy(true);
    try {
      await api.put("/recaps", { period, entries });
      toast.success(`Rekap ${periodLabel(period)} tersimpan (${entries.length} sel)`);
      setDirty({});
      loadDebts();
      setLock((await api.get("/recaps", { params: { period } })).data.lock);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const onKey = (e, r, c) => {
    if (e.key === "Enter") {
      e.preventDefault();
      document.querySelector(`[data-cell="${r + 1}-${c}"]`)?.focus();
    }
  };

  const types = data?.types || [];
  const colTotal = (vid) => rows.reduce((s, c) => s + (cells[`${c.id}|${vid}`] || 0), 0);
  const rowQty = (cid) => types.reduce((s, v) => s + (cells[`${cid}|${v.id}`] || 0), 0);
  const priceOf = (k, v) => (dirty[k] ? v.price : snap[k] ?? v.price);
  const rowVal = (cid) => types.reduce((s, v) => s + (cells[`${cid}|${v.id}`] || 0) * priceOf(`${cid}|${v.id}`, v), 0);
  const grandQty = rows.reduce((s, c) => s + rowQty(c.id), 0);
  const grandVal = rows.reduce((s, c) => s + rowVal(c.id), 0);
  const nDirty = Object.keys(dirty).length;
  const showDebt = can("debt:read");
  const canPay = can("payment:write");
  const canFix = canPay && can("payment:partial") && lock?.stage === "draft";
  const debtTotal = rows.reduce((s, c) => s + Math.max(debts[c.id]?.remaining || 0, 0), 0);
  const paidTotal = rows.reduce((s, c) => s + (debts[c.id]?.paid || 0), 0);

  return (
    <div>
      <PageHeader sheet="SHEET 03" title="Rekap Penjualan Voucher" subtitle="Isi jumlah voucher yang BENAR-BENAR terjual (penjualan aktual). Angka ini dipakai untuk menghitung uang top up. Pasokan awal pelanggan baru tidak diisi di sini. Tekan Enter untuk pindah ke baris bawah.">
        <PeriodInput value={period} onChange={setPeriod} />
        {can("export:run") && <Button variant="outline" onClick={() => downloadCsv("/export/recap", `rekap-${period}`, { period })} data-testid="recap-export-btn"><Download className="h-4 w-4 mr-2" />Export</Button>}
        {editable && <ImportDialog kind="recap" period={period} onDone={load} />}
        {editable && <Button onClick={save} disabled={!nDirty || busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="recap-save-btn"><Save className="h-4 w-4 mr-2" />Simpan {nDirty ? `(${nDirty})` : ""}</Button>}
      </PageHeader>
      {lock && <div className="mb-3 space-y-2"><PeriodStageBar lock={lock} onChange={setLock} /><RecapLockBanner lock={lock} onChange={setLock} /></div>}
      {!data ? <Loading /> : !data.customers.length || !types.length ? (
        <Empty>Tambahkan pelanggan dan jenis voucher terlebih dahulu.</Empty>
      ) : (
        <div className="space-y-3 fade-up">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Pelanggan" value={num(rows.length)} testId="recap-stat-customers" />
            <StatPill label="Total Qty Terjual" value={num(grandQty)} tone="blue" testId="recap-stat-qty" />
            <StatPill label="Total Nilai" value={rupiah(grandVal)} tone="green" testId="recap-stat-value" />
            <button onClick={() => nav("/estimasi")} className="border border-emerald-600 bg-emerald-700 text-white rounded-sm px-4 py-3 text-left hover:bg-emerald-800 transition-colors" data-testid="recap-go-estimate">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-100">Langkah berikutnya</div>
              <div className="font-display font-semibold mt-1 flex items-center">Estimasi +30% <ArrowRight className="h-4 w-4 ml-2" /></div>
            </button>
          </div>
          <div className="relative w-full sm:w-64"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" /><Input placeholder="Filter pelanggan..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="recap-search-input" /></div>
          <NativeSelect value={stF} onChange={(e) => setStF(e.target.value)} className="sm:ml-2 w-full sm:w-auto" data-testid="recap-filter-status">
            <option value="">Semua status pelanggan</option>
            {Object.entries(CUST_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </NativeSelect>
          <div className="xl-wrap max-h-[62vh]">
            <table className="xl" data-testid="recap-grid">
              <thead>
                <tr className="xl-letters"><th className="rownum" />{["Pelanggan", "Status", "Alamat", ...types, "Qty", "Rp", ...(showDebt ? ["Bayar", "Hutang", "Status"] : [])].map((_, i) => <th key={i} className={i === 0 ? "xl-sticky" : ""}>{colLetter(i)}</th>)}{showDebt && canPay && <th />}</tr>
                <tr>
                  <th className="rownum">#</th><th className="xl-sticky">Pelanggan</th><th>Status</th><th>Alamat</th>
                  {types.map((v) => <th key={v.id} className="text-right">{v.name}<div className="font-mono normal-case text-[10px] text-emerald-700">@ {num(v.price)}</div></th>)}
                  <th className="text-right">Total Qty</th><th className="text-right">Total (Rp)</th>
                  {showDebt && <><th className="text-right">Dibayar</th><th className="text-right">Hutang</th><th>Status</th>{canPay && <th>Aksi</th>}</>}
                </tr>
              </thead>
              <tbody>
                {rows.map((c, ri) => (
                  <tr key={c.id}>
                    <td className="rownum">{ri + 1}</td>
                    <td className="font-medium xl-sticky">{c.name}</td>
                    <td><CustStatusBadge status={statuses[c.id]?.status} testId={`recap-status-${ri}`} /></td>
                    <td className="text-slate-600 text-xs">{c.address_name}</td>
                    {types.map((v, ci) => {
                      const k = `${c.id}|${v.id}`;
                      return (
                        <td key={v.id} className="cell-edit">
                          <input type="number" min={0} className={`xl-input ${dirty[k] ? "dirty" : ""}`} value={cells[k] || ""} placeholder="0"
                            onChange={(e) => setCell(k, e.target.value)} onKeyDown={(e) => onKey(e, ri, ci)} disabled={!editable}
                            data-cell={`${ri}-${ci}`} data-testid={`recap-cell-${ri}-${ci}`} />
                        </td>
                      );
                    })}
                    <td className="num font-semibold">{num(rowQty(c.id))}</td>
                    <td className="num">{num(rowVal(c.id))}</td>
                    {showDebt && <DebtCells c={c} ri={ri} d={debts[c.id]} canPay={canPay} canFix={canFix} dirty={nDirty > 0} onPay={(d) => setPayTarget({ ...d, customer_id: c.id, customer_name: c.name, period })} onFix={(d) => setFixTarget({ ...d, customer_id: c.id, customer_name: c.name, period })} />}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="rownum">Σ</td><td colSpan={3}>TOTAL</td>
                  {types.map((v) => <td key={v.id} className="num" data-testid={`recap-total-${v.id}`}>{num(colTotal(v.id))}</td>)}
                  <td className="num" data-testid="recap-grand-qty">{num(grandQty)}</td><td className="num" data-testid="recap-grand-value">{num(grandVal)}</td>
                  {showDebt && <><td className="num">{num(paidTotal)}</td><td className="num text-rose-700" data-testid="recap-debt-total">{num(debtTotal)}</td><td colSpan={canPay ? 2 : 1} /></>}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
      <PaymentDialog target={payTarget} onClose={() => setPayTarget(null)} onDone={loadDebts} />
      <PaidCorrectDialog target={fixTarget} onClose={() => setFixTarget(null)} onDone={loadDebts} />
    </div>
  );
}

function DebtCells({ d, ri, canPay, canFix, dirty, onPay, onFix }) {
  const remaining = d?.remaining || 0;
  return (
    <>
      <td className="num">
        <span className="inline-flex items-center gap-1 justify-end">
          {num(d?.paid || 0)}
          {canFix && d?.due > 0 && (
            <button type="button" disabled={dirty} title={dirty ? "Simpan rekap terlebih dahulu" : "Koreksi keterangan bayar"} onClick={() => onFix(d)} className="p-1 text-emerald-700 hover:bg-emerald-50 rounded-sm disabled:opacity-40" data-testid={`recap-paid-edit-${ri}`}><Pencil className="h-3 w-3" /></button>
          )}
        </span>
      </td>
      <td className={`num ${remaining > 0 ? "text-rose-700 font-semibold bg-rose-50" : ""}`} data-testid={`recap-debt-${ri}`}>{num(Math.max(remaining, 0))}</td>
      <td><DebtBadge status={d?.status} testId={`recap-debt-status-${ri}`} /></td>
      {canPay && <td>{remaining > 0 && (
        <Button size="sm" variant="outline" className="h-6 text-xs" disabled={dirty} title={dirty ? "Simpan rekap terlebih dahulu" : ""} onClick={() => onPay(d)} data-testid={`recap-pay-${ri}`}>Bayar</Button>
      )}</td>}
    </>
  );
}
